import { atom, type Atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

export interface AdminAuthUserMeta {
  id: string;
  fullName: string;
  userId: string;
  permissions: string[];
  // No token field — JWT is stored server-side in BFF session
}

// localStorage key changed from 'admin_auth_user' to 'admin_auth_user_meta' to prevent
// stale token data from persisting across the upgrade to server-side session storage.
export const authUserMetaAtom = atomWithStorage<AdminAuthUserMeta | null>('admin_auth_user_meta', null);
export const isRehydratingAtom = atom(false);

// In-memory only — never persisted. Populated from login/SessionStatus response body
// so cross-origin deployments can use the double-submit CSRF pattern without relying
// on document.cookie (which only returns cookies for the current domain).
export const csrfTokenAtom = atom<string | null>(null);
export const isAuthenticatedAtom = atom((get) => get(authUserMetaAtom) !== null);
// Cached per access string. Returning a fresh atom on every call would make
// useAtomValue resubscribe on every render and blow React's update depth.
const permissionAtoms = new Map<string, Atom<boolean>>();

export const hasPermissionAtom = (accessString: string): Atom<boolean> => {
  let permissionAtom = permissionAtoms.get(accessString);
  if (!permissionAtom) {
    permissionAtom = atom(
      (get) => get(authUserMetaAtom)?.permissions.includes(`${accessString}.Full`) ?? false,
    );
    permissionAtoms.set(accessString, permissionAtom);
  }
  return permissionAtom;
};
